import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Eye, EyeOff, CheckCircle, Lock } from 'lucide-react';
import axios from 'axios';

const API = window.location.origin;

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(
    token ? '' : 'Invalid reset link. Please request a new password reset.',
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 bg-[#050505] flex items-center justify-center" data-testid="reset-password-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md mx-auto px-6"
      >
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-8 text-sm"
        >
          <ArrowLeft size={16} /> Back to Login
        </Link>

        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl text-white mb-2">Create New Password</h1>
          <p className="text-white/60 text-sm">
            Please enter your new password below.
          </p>
        </div>

        <div className="bg-[#0a0a0a] border border-white/10 p-8">
          {success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h2 className="font-heading text-xl text-white mb-2">Password Reset Complete</h2>
              <p className="text-white/60 text-sm mb-6">
                Your password has been successfully reset. You can now log in with your new password.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-4 bg-[#D4AF37] text-black text-sm uppercase tracking-widest hover:bg-[#C9A432] transition-colors"
                data-testid="go-to-login"
              >
                Go to Login
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                  {error}
                </div>
              )}

              {!token ? (
                <div className="text-center py-4">
                  <p className="text-white/60 mb-4">Invalid or missing reset token.</p>
                  <Link 
                    to="/forgot-password" 
                    className="text-[#D4AF37] hover:underline"
                  >
                    Request a new reset link
                  </Link>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">New Password</label>
                    <div className="relative">
                      <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full bg-transparent border-b border-white/20 py-3 pl-8 pr-10 text-white placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37] transition-colors"
                        placeholder="Enter new password"
                        minLength={8}
                        data-testid="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-0 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    <p className="text-white/40 text-xs mt-2">Must be at least 8 characters</p>
                  </div>

                  <div>
                    <label className="block text-white/60 text-xs uppercase tracking-widest mb-2">Confirm Password</label>
                    <div className="relative">
                      <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full bg-transparent border-b border-white/20 py-3 pl-8 pr-10 text-white placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37] transition-colors"
                        placeholder="Confirm new password"
                        data-testid="confirm-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-0 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                      >
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 bg-[#D4AF37] text-black text-sm uppercase tracking-widest hover:bg-[#C9A432] transition-colors flex items-center justify-center"
                    data-testid="reset-submit"
                  >
                    {loading ? (
                      <span className="animate-spin w-5 h-5 border-2 border-black border-t-transparent rounded-full" />
                    ) : (
                      'Reset Password'
                    )}
                  </button>
                </>
              )}
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};
